import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle2, ArrowRight, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="bg-white rounded-3xl border border-[#E7E5E4] p-10 text-center">
          <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl text-[#1C1917] mb-2">Enlace inválido</h1>
          <p className="text-[#57534E] text-sm mb-6">Este enlace no incluye un token válido. Solicita uno nuevo desde "Olvidé mi contraseña".</p>
          <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
            <Link to="/recuperar-contrasena">Solicitar nuevo enlace</Link>
          </Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'No pudimos restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden">
        {done ? (
          <div className="p-10 text-center" data-testid="reset-done">
            <div className="w-14 h-14 rounded-full bg-[#F7F2E8] mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#2A5C43]" />
            </div>
            <h1 className="font-display font-bold text-2xl text-[#1C1917] mb-2">¡Listo!</h1>
            <p className="text-[#57534E] text-sm mb-6">Tu contraseña fue restablecida. En unos segundos te llevamos a la pantalla de inicio de sesión.</p>
            <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
              <Link to="/login">Iniciar sesión <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-[#1C1917] text-white px-8 py-8">
              <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-1">Restablecer</p>
              <h1 className="font-display font-bold text-2xl">Crea una nueva contraseña</h1>
              <p className="text-sm text-white/85 mt-2">Elige una contraseña segura que no hayas usado antes.</p>
            </div>

            <form onSubmit={submit} className="p-8 space-y-4">
              <div>
                <Label htmlFor="pw" className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Nueva contraseña</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
                  <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" data-testid="reset-password" />
                </div>
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Repite la contraseña</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
                  <Input id="pw2" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10" data-testid="reset-password-confirm" />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="reset-error">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full py-5 font-semibold"
                data-testid="reset-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Restablecer contraseña
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
